import type { DocumentRealtimeAction } from "../api/realtime";
import type { DocumentStatus } from "../types";

const SOUND_STORAGE_KEY = "orderflow_sound_enabled";

let audioCtx: AudioContext | null = null;
let lastPlayedKey = "";
let lastPlayedAt = 0;

function getAudioContext(): AudioContext | null {
  if (typeof window === "undefined") return null;
  const AudioContextClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextClass) return null;
  if (!audioCtx) {
    audioCtx = new AudioContextClass();
  }
  if (audioCtx.state === "suspended") {
    void audioCtx.resume().catch(() => undefined);
  }
  return audioCtx;
}

// Ensure AudioContext resumes upon first user gesture in browser
if (typeof window !== "undefined") {
  const unlockAudio = () => {
    if (audioCtx && audioCtx.state === "suspended") {
      void audioCtx.resume().catch(() => undefined);
    }
  };
  window.addEventListener("click", unlockAudio, { once: true, passive: true });
  window.addEventListener("keydown", unlockAudio, { once: true, passive: true });
}

export function isSoundEnabled(): boolean {
  if (typeof localStorage === "undefined") return true;
  return localStorage.getItem(SOUND_STORAGE_KEY) !== "false";
}

export function setSoundEnabled(enabled: boolean): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(SOUND_STORAGE_KEY, enabled ? "true" : "false");
  window.dispatchEvent(new CustomEvent("orderflow-sound-toggle", { detail: { enabled } }));
}

export function testNotificationSound(): void {
  playTones([
    { freq: 659.25, start: 0.00, duration: 0.20, type: "sine", gain: 0.75 },
    { freq: 880.00, start: 0.10, duration: 0.40, type: "sine", gain: 0.85 },
  ]);
}

interface Tone {
  freq: number;
  start: number;
  duration: number;
  type?: OscillatorType;
  gain?: number;
}

function playTones(tones: Tone[]): void {
  if (!isSoundEnabled()) return;
  const ctx = getAudioContext();
  if (!ctx) return;

  const masterGain = ctx.createGain();
  masterGain.gain.setValueAtTime(0.22, ctx.currentTime);
  masterGain.connect(ctx.destination);

  const startTime = ctx.currentTime + 0.01;

  for (const tone of tones) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = tone.type ?? "sine";
    osc.frequency.setValueAtTime(tone.freq, startTime + tone.start);

    const toneStart = startTime + tone.start;
    const toneDuration = tone.duration;
    const peakGain = tone.gain ?? 0.8;

    gain.gain.setValueAtTime(0.0001, toneStart);
    // Smooth, gentle attack
    gain.gain.exponentialRampToValueAtTime(peakGain, toneStart + 0.02);
    // Natural acoustic decay
    gain.gain.exponentialRampToValueAtTime(0.0001, toneStart + toneDuration);

    osc.connect(gain);
    gain.connect(masterGain);

    osc.start(toneStart);
    osc.stop(toneStart + toneDuration);
  }
}

/**
 * Plays distinct, harmonic notification sounds based on document event or status.
 * Deduplicates multiple rapid calls (e.g. from multiple component listeners).
 */
export function playDocumentEventSound(
  action: DocumentRealtimeAction | string,
  status?: DocumentStatus | string,
  documentId?: string,
): void {
  const now = Date.now();
  const dedupKey = `${documentId || ""}_${action}_${status || ""}`;
  if (dedupKey === lastPlayedKey && now - lastPlayedAt < 600) {
    return;
  }
  lastPlayedKey = dedupKey;
  lastPlayedAt = now;

  const actStr = String(action).toLowerCase();
  const statStr = String(status || "").toLowerCase();

  // 1. Approved / Signed / Accepted: Harmonious C-major arpeggio (C5 -> E5 -> G5 -> C6)
  if (
    actStr === "document_approved" ||
    actStr.includes("approv") ||
    actStr.includes("accept") ||
    statStr === "signed" ||
    statStr === "accepted"
  ) {
    playTones([
      { freq: 523.25, start: 0.00, duration: 0.28, type: "sine", gain: 0.7 },
      { freq: 659.25, start: 0.08, duration: 0.32, type: "sine", gain: 0.8 },
      { freq: 783.99, start: 0.16, duration: 0.38, type: "sine", gain: 0.85 },
      { freq: 1046.50, start: 0.24, duration: 0.65, type: "triangle", gain: 0.9 },
    ]);
    return;
  }

  // 2. Fix Required / Fix Requested: Clear two-tone alert chime (A5 -> F5)
  if (
    actStr === "document_fix_requested" ||
    actStr.includes("fix") ||
    statStr === "fix_requested" ||
    statStr.includes("fix")
  ) {
    playTones([
      { freq: 880.00, start: 0.00, duration: 0.22, type: "sine", gain: 0.8 },
      { freq: 698.46, start: 0.12, duration: 0.45, type: "triangle", gain: 0.85 },
    ]);
    return;
  }

  // 3. Revision Pending / Re-uploaded: Upward refreshing triad (D5 -> F#5 -> A5)
  if (
    actStr === "document_reuploaded" ||
    actStr.includes("reupload") ||
    actStr.includes("revision")
  ) {
    playTones([
      { freq: 587.33, start: 0.00, duration: 0.22, type: "sine", gain: 0.7 },
      { freq: 739.99, start: 0.09, duration: 0.25, type: "sine", gain: 0.8 },
      { freq: 880.00, start: 0.18, duration: 0.50, type: "sine", gain: 0.9 },
    ]);
    return;
  }

  // 4. Rejected: Gentle warm downward tones (F5 -> D5)
  if (
    actStr === "document_rejected" ||
    actStr.includes("reject") ||
    statStr === "rejected"
  ) {
    playTones([
      { freq: 698.46, start: 0.00, duration: 0.22, type: "sine", gain: 0.75 },
      { freq: 587.33, start: 0.12, duration: 0.45, type: "sine", gain: 0.7 },
    ]);
    return;
  }

  // 5. Uploaded / New Pending: Soft double bell chime (E5 -> A5)
  if (
    actStr === "document_uploaded" ||
    actStr.includes("upload") ||
    statStr === "pending"
  ) {
    playTones([
      { freq: 659.25, start: 0.00, duration: 0.24, type: "sine", gain: 0.75 },
      { freq: 880.00, start: 0.10, duration: 0.45, type: "sine", gain: 0.85 },
    ]);
    return;
  }

  // 6. AI Extracted: Futuristic dual sparkle
  if (actStr === "document_extracted" || actStr.includes("extract")) {
    playTones([
      { freq: 783.99, start: 0.00, duration: 0.18, type: "sine", gain: 0.6 },
      { freq: 987.77, start: 0.08, duration: 0.35, type: "sine", gain: 0.7 },
    ]);
    return;
  }

  // Default subtle chime
  playTones([
    { freq: 880.00, start: 0.00, duration: 0.25, type: "sine", gain: 0.7 },
  ]);
}
